import React, { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import ExtratoMotorista from "./pages/ExtratoMotorista";
import AdminPanel from "./pages/AdminPanel";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import { recordMapDiagnostic } from "./services/mapDiagnostics";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import LandingPage from "./pages/LandingPage";

import Terms   from "./legal/Terms";
import Privacy from "./legal/Privacy";
import DriverTerms from "./legal/DriverTerms";
import CommunityGuidelines from "./legal/CommunityGuidelines";
import CommercialPolicies from "./legal/CommercialPolicies";
import DeleteAccount from "./pages/DeleteAccount";

import PlanoMotorista    from "./pages/PlanoMotorista";
import SplashScreen      from "./components/SplashScreen";
import Login             from "./components/Login";
import LocationPermission from "./components/LocationPermission";
import PassengerHome     from "./components/PassengerHome";
import DriverHome        from "./pages/DriverHome";
import EscolhaDestino    from "./pages/EscolhaDestino";
import ProtectedRoute    from "./components/ProtectedRoute";
import Activity          from "./pages/Activity";
import Conta             from "./pages/Conta";
import CadastroMotorista from "./components/CadastroMotorista";
import PassengerRegister from "./components/PassengerRegister";
import AcompanharViagem  from "./pages/AcompanharViagem";
import PassengerPremium from "./pages/PassengerPremium";
import PlanCampaign from "./pages/PlanCampaign";
import DriverVerificationReturn from "./pages/DriverVerificationReturn";
import DriverApplicationStatus from "./pages/DriverApplicationStatus";
import { getCachedProfilePhoto, setCachedProfilePhoto } from "./services/profileStore";
import PassengerExperienceGate from "./components/PassengerExperienceGate";
import DriverLegalGate from "./components/DriverLegalGate";
import { resolvePushDestination } from "./services/pushNavigation";
import { registrarListenersNotificacoesNativas } from "./services/notificationService";
import { isStoreScreenshotCapture, storeScreenshotUser } from "./screenshotSession";
import { markPerformance } from "./services/performanceTrace";
import { bindClientStorageToUser } from "./services/clientStorageSecurity";

const libraries = ["places"];

function PushNavigationBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    registrarListenersNotificacoesNativas().catch(error => {
      console.error("Erro ao registrar listeners de notificações:", { code: error?.code || "push/listeners" });
    });

    const handlePushAction = (event) => {
      const data = event?.detail || {};
      const destination = resolvePushDestination(data);
      if (!destination) return;
      if (data.rideId) localStorage.setItem("activeRideId", String(data.rideId));
      navigate(destination);
    };
    window.addEventListener("zuno:push-action", handlePushAction);
    return () => window.removeEventListener("zuno:push-action", handlePushAction);
  }, [navigate]);

  return null;
}

function WebMapsRuntime({ onError }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    libraries,
    language: "pt-BR",
    region: "BR",
  });

  useEffect(() => {
    const mapKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";
    recordMapDiagnostic("MAP_SCRIPT_REQUEST_STARTED", {
      mapScript: "REQUESTED",
      href: window.location.href,
      origin: window.location.origin,
      referrer: document.referrer || "(empty)",
      userAgent: navigator.userAgent,
      keyPresent: Boolean(mapKey),
      keyLength: mapKey.length,
      keyPrefix: mapKey ? mapKey.slice(0, 4) : "",
      keySuffix: mapKey ? mapKey.slice(-4) : "",
    });
  }, []);

  useEffect(() => {
    if (isLoaded) recordMapDiagnostic("MAP_SCRIPT_LOADED", { mapScript: "LOADED" });
  }, [isLoaded]);

  useEffect(() => {
    if (!loadError) return;
    recordMapDiagnostic("MAP_SCRIPT_ERROR", { mapScript: "ERROR", failureType: "SCRIPT_NOT_LOADED", error: loadError });
    const detail = { code: loadError?.code || "maps/script", message: loadError?.message || "Google Maps script failed", origin: window.location.origin };
    console.error("[ZunoMap]", { stage: "MAP_SCRIPT_ERROR", ...detail });
    window.dispatchEvent(new CustomEvent("zuno:map-error", { detail }));
    onError(loadError);
  }, [loadError, onError]);

  return null;
}

function App() {
  const [destino, setDestino] = useState(null);
  // Vive acima das rotas: navegar e remontar a Home não reinicia a intro.
  const [passengerIntroCompleted, setPassengerIntroCompleted] = useState(false);
  const [webMapError, setWebMapError] = useState(null);

  // A restauração do Firebase acontece em segundo plano. A interface nunca deve
  // ficar bloqueada esperando onAuthStateChanged: se já houver uma sessão em
  // memória ela é usada, caso contrário o fluxo público abre imediatamente.
  const [user, setUser] = useState(
    isStoreScreenshotCapture ? storeScreenshotUser : auth.currentUser || null,
  );

  useEffect(() => {
    if (isStoreScreenshotCapture) return undefined;
    markPerformance("AUTH_INIT_START", {}, { onlyOnce: true });
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      markPerformance("AUTH_STATE_RESOLVED", { authenticated: Boolean(currentUser) }, { onlyOnce: true });
      if (currentUser) {
        bindClientStorageToUser(currentUser.uid);
        localStorage.setItem("usuarioLogado", currentUser.displayName || currentUser.email?.split("@")[0] || "");
        if (!getCachedProfilePhoto() && currentUser.photoURL) setCachedProfilePhoto(currentUser.photoURL);
        localStorage.setItem("userEmail",     currentUser.email    || "");
        setUser(currentUser);
      } else {
        bindClientStorageToUser(null);
        setUser(null);
      }
      markPerformance("AUTH_INIT_DONE", { authenticated: Boolean(currentUser) }, { onlyOnce: true });
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isStoreScreenshotCapture) return undefined;
    const signOutScreenshotSession = () => setUser(null);
    window.addEventListener("zuno:store-screenshot-sign-out", signOutScreenshotSession);
    return () => window.removeEventListener("zuno:store-screenshot-sign-out", signOutScreenshotSession);
  }, []);

  const estaLogado  = !!user;
  const userType    = localStorage.getItem("userType");
  const homeCorreta = estaLogado
    ? userType === "driver" ? "/driver-home" : "/passenger-home"
    : "/login";
  const isNativeApp = Capacitor.isNativePlatform();
  const usesNativeIOSMap = Capacitor.getPlatform() === "ios";

  if (webMapError) return (
    <div style={{ color: "white", background: "#e53935", padding: "20px", fontFamily: "sans-serif" }}>
      ⚠️ Erro ao carregar o Google Maps. Verifique a chave da API.
    </div>
  );

  return (
    <Router>
      {!usesNativeIOSMap && <WebMapsRuntime onError={setWebMapError} />}
      <PushNavigationBridge />
      <Routes>
        <Route path="/admin" element={<AdminPanel />} />

        {/* ✅ Raiz — Landing Page pública */}
        <Route
          path="/"
          element={
            isStoreScreenshotCapture
              ? <Navigate to="/passenger-home" replace />
              : isNativeApp
                ? <Navigate to={estaLogado ? homeCorreta : "/splash"} replace />
                : <LandingPage />
          }
        />
        <Route path="/inicio" element={<LandingPage />} />

        {/* Legais */}
        <Route path="/termos"      element={<Terms />} />
        <Route path="/privacidade" element={<Privacy />} />
        <Route path="/termos-motorista" element={<DriverTerms />} />
        <Route path="/diretrizes-comunidade" element={<CommunityGuidelines />} />
        <Route path="/pagamentos" element={<CommercialPolicies kind="payments" />} />
        <Route path="/cancelamento-reembolso" element={<CommercialPolicies kind="cancellation" />} />
        <Route path="/cookies" element={<CommercialPolicies kind="cookies" />} />
        <Route path="/excluir-conta" element={<DeleteAccount />} />

        {/* Plano do motorista */}
        <Route path="/plano-motorista" element={<PlanoMotorista />} />

        {/* Permissão de localização */}
        <Route
          path="/location-permission"
          element={
            estaLogado
              ? <Navigate to={homeCorreta} replace />
              : <LocationPermission />
          }
        />
        <Route path="/extrato-motorista" element={<ExtratoMotorista />} />

        {/* Acompanhamento de viagem — público, sem login */}
        <Route path="/acompanhar/:token" element={<AcompanharViagem />} />

        {/* Splash / Onboarding */}
        <Route
          path="/splash"
          element={
            estaLogado
              ? <Navigate to={homeCorreta} replace />
              : <SplashScreen estaLogado={estaLogado} homeCorreta={homeCorreta} />
          }
        />

        {/* Login */}
        <Route
          path="/login"
          element={
            estaLogado
              ? <Navigate to={homeCorreta} replace />
              : <Login />
          }
        />

        {/* Cadastro e retorno KYC exigem uma sessão Firebase autenticada. */}
        <Route path="/cadastro-motorista" element={
          <ProtectedRoute estaLogado={estaLogado} tipoNecessario="driver">
            <CadastroMotorista />
          </ProtectedRoute>
        } />
        <Route path="/motorista-verificacao-retorno" element={
          <ProtectedRoute estaLogado={estaLogado} tipoNecessario="driver">
            <DriverVerificationReturn />
          </ProtectedRoute>
        } />

        {/* Análise do cadastro */}
        <Route path="/motorista-analise" element={
          <ProtectedRoute estaLogado={estaLogado} tipoNecessario="driver">
            <DriverApplicationStatus />
          </ProtectedRoute>
        } />

        {/* Home do passageiro */}
        <Route
          path="/passenger-home"
          element={
            <ProtectedRoute estaLogado={estaLogado} tipoNecessario="passenger">
              <PassengerExperienceGate>
                <PassengerHome
                  showSessionIntro={!passengerIntroCompleted}
                  onSessionIntroContinue={() => setPassengerIntroCompleted(true)}
                />
              </PassengerExperienceGate>
            </ProtectedRoute>
          }
        />

        {/* Home do motorista */}
        <Route
          path="/driver-home"
          element={
            <ProtectedRoute estaLogado={estaLogado} tipoNecessario="driver">
              <DriverLegalGate><DriverHome /></DriverLegalGate>
            </ProtectedRoute>
          }
        />

        {/* Atividade */}
        <Route
          path="/atividade"
          element={
            <ProtectedRoute estaLogado={estaLogado} tipoNecessario="passenger">
              <Activity />
            </ProtectedRoute>
          }
        />

        {/* Conta */}
        <Route
          path="/conta"
          element={
            <ProtectedRoute estaLogado={estaLogado}>
              <Conta />
            </ProtectedRoute>
          }
        />

        {/* Campanha Zuno Premium */}
        <Route
          path="/zuno-premium"
          element={
            <ProtectedRoute estaLogado={estaLogado} tipoNecessario="passenger">
              <PassengerPremium />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plano-mensalidade"
          element={
            <ProtectedRoute estaLogado={estaLogado} tipoNecessario="passenger">
              <PlanCampaign plan="mensalidade" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plano-flex"
          element={
            <ProtectedRoute estaLogado={estaLogado} tipoNecessario="passenger">
              <PlanCampaign plan="flex" />
            </ProtectedRoute>
          }
        />

        {/* Destino */}
        <Route
          path="/destino"
          element={
            <ProtectedRoute estaLogado={estaLogado} tipoNecessario="passenger">
              <EscolhaDestino destino={destino} setDestino={setDestino} />
            </ProtectedRoute>
          }
        />

        {/* Enviar documentos — público */}
        <Route path="/enviar-documentos" element={<Navigate to="/cadastro-motorista" replace />} />

        {/* Completar perfil após SMS */}
        <Route
          path="/completar-perfil"
          element={
            estaLogado
              ? <PassengerRegister />
              : <Navigate to="/login" replace />
          }
        />

        {/* Qualquer rota desconhecida → home correta */}
        <Route path="*" element={<Navigate to={homeCorreta} replace />} />

      </Routes>
    </Router>
  );
}

export default App;
